"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CREATE_MEETING_PREP, GET_MEETING_PREP, getMeetingPrep } from "@/api";
import { routes } from "@/constants";
import { CloudEventType } from "@/constants/websocket-events";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import type { MeetingPrepGeneration } from "@/hooks/useMeetingPrep";
import { isMeetingPrepGenerating } from "@/hooks/useMeetingPrepGeneration";
import type { CloudEvent } from "@/types/cloud-events";
import { MeetingPrepStatus, type MeetingPrepEntity } from "@/types";

/**
 * The prep's own details page, or the loader page waiting on this prep, which
 * opens it once it is ready. Read at decision time, after the status fetch.
 */
const isWatchingPrep = (
  queryClient: QueryClient,
  prep: Pick<MeetingPrepEntity, "id" | "meetingId">,
): boolean => {
  const { pathname, search } = window.location;
  if (pathname === routes.meetingPrep.details(prep.id)) return true;
  if (pathname !== routes.meetingPrep.index) return false;
  const params = new URLSearchParams(search);
  const meetingId = params.get("meetingId");
  if (meetingId) return meetingId === prep.meetingId;
  // A `creating` loader names its create request, not the prep it made.
  const generationId = params.get("generationId");
  if (!generationId) return false;
  return queryClient
    .getMutationCache()
    .findAll({ mutationKey: [CREATE_MEETING_PREP] })
    .some(
      (mutation) =>
        (mutation.state.variables as MeetingPrepGeneration | undefined)
          ?.generationId === generationId &&
        (mutation.state.data as MeetingPrepEntity | undefined)?.id === prep.id,
    );
};

/** Tells an advisor who has moved on that a prep finished generating. */
export const useMeetingPrepStatusWebSocketSubscription = (): void => {
  const queryClient = useQueryClient();
  const router = useRouter();

  const handleMessage = useCallback(
    async (event: CloudEvent) => {
      if (event.type !== CloudEventType.MEETING_PREP_UPDATED) return;
      const meetingPrepId = (event.data as { meetingPrepId?: string })
        ?.meetingPrepId;
      if (!meetingPrepId) return;

      // The event carries no status, and fires for every edit too. Only a
      // prep this tab last saw generating can have just finished; the fetch
      // below replaces that cached status, so a redelivery stays quiet.
      const queryKey = [GET_MEETING_PREP, meetingPrepId];
      const cached = queryClient.getQueryData<MeetingPrepEntity | null>(
        queryKey,
      );
      if (
        !cached ||
        !isMeetingPrepGenerating(cached) ||
        isWatchingPrep(queryClient, cached)
      ) {
        return;
      }

      let prep: MeetingPrepEntity | null;
      try {
        prep = await queryClient.fetchQuery({
          queryKey,
          queryFn: () => getMeetingPrep(meetingPrepId),
          staleTime: 0,
        });
      } catch {
        return;
      }
      if (
        prep?.status !== MeetingPrepStatus.Ready &&
        prep?.status !== MeetingPrepStatus.Failed
      ) {
        return;
      }
      if (isWatchingPrep(queryClient, prep)) return;

      const options = {
        id: `meeting-prep-status-${meetingPrepId}-${prep.status}`,
        description: prep.title || undefined,
        duration: 10000,
        closeButton: true,
        action: {
          label: "Open",
          onClick: () => router.push(routes.meetingPrep.details(meetingPrepId)),
        },
      };
      if (prep.status === MeetingPrepStatus.Failed) {
        toast.error("Meeting prep generation failed.", options);
      } else {
        toast.success("Meeting prep generation complete.", options);
      }
    },
    [queryClient, router],
  );

  useWebSocketSubscription("meeting-prep", undefined, handleMessage);
};

export const MeetingPrepStatusWebSocketBridge = () => {
  useMeetingPrepStatusWebSocketSubscription();
  return null;
};
